import React, {useState, useEffect} from "react";
import * as THREE from "three";
import bluesky from "images/bluesky.png";
import ParticlePropertyComponent from "./Components/ParticlePropertyComponent";
import ParticleBehaviorComponent from "./Components/ParticleBehaviorComponent";
import MaterialPropertyComponent from "./Components/MaterialPropertyComponent";
import EmitterPropertyComponent from "./Components/EmitterPropertyComponent";
import PostProcessingComponent from "./Components/PostProcessingComponent";
import ToneMappingComponent from "./Components/ToneMappingComponent";
import EnvironmentComponent from "./Components/EnvironmentComponent";
import BackgroundComponent from "./Components/BackgroundComponent";
import SpriteTextComponent from "./Components/SpriteTextComponent";
import ObjectDataComponent from "./Components/ObjectDataComponent";
import AppearanceComponent from "./Components/AppearanceComponent";
import TransformComponent from "./Components/TransformComponent";
import BehaviorComponent from "./Components/BehaviorComponent";
import PhysicsComponent from "./Components/PhysicsComponent";
import TextureComponent from "./Components/TextureComponent";
import ScriptComponent from "./Components/ScriptComponent";
import PortalComponent from "./Components/PortalComponent";
import CameraComponent from "./Components/CameraComponent";
import AudioComponent from "./Components/AudioComponent";
import VideoComponent from "./Components/VideoComponent";
import LightComponent from "./Components/LightComponent";
import TitleComponent from "./Components/TitleComponent";
import FogComponent from "./Components/FogComponent";
import styles from "./index.module.scss";
import {PROPERTY_TAB} from "utils";

const View = ({
  currentTab,
  selectedObject,
  scene,
  versionCompatibility,
  backgroundColor,
  textOnChange,
  transformOnChange,
  colorOnChange,
  checkboxOnChange,
  inputOnChange,
  dropdownOnChange,
  // isAudioPlaying,
  // playPreviewAudio,
  // stopPreviewAudio,
  isMaterialTabOpen,
  addUserScript,
  setScriptIndex,
  editUserScript,
  removeUserScript,
}) => {
  const [dropdownOpened, setDropdownOpened] = useState(null);

  useEffect(() => {
    const onClickOutside = () => {
      setDropdownOpened(null);
    };
    document.addEventListener("click", onClickOutside);
    return () => {
      document.removeEventListener("click", onClickOutside);
    };
  }, []);

  const isObjectScripting = scene.userData.codeEditorMode === "text" && versionCompatibility.script.inObject;

  // Texture
  let textureImage = "";
  let active = "";
  if (scene.background instanceof THREE.CubeTexture) {
    textureImage = bluesky;
    active = "Default";
  } else if (scene.background instanceof THREE.Texture) {
    textureImage = scene.background.image?.currentSrc;
    active = "Texture";
  } else {
    textureImage = bluesky;
    active = "Color";
  }

  // Scene Property tab - Do not pass selected object in this component
  if (currentTab === PROPERTY_TAB.SCENE) {
    return (
      <div className={styles.container}>
        <BackgroundComponent
          active={active}
          backgroundColor={backgroundColor}
          textureImage={textureImage}
          inputOnChange={inputOnChange}
          colorOnChange={colorOnChange}
        />
        <EnvironmentComponent scene={scene} inputOnChange={inputOnChange} checkboxOnChange={checkboxOnChange} />
        <FogComponent
          scene={scene}
          inputOnChange={inputOnChange}
          dropdownOnChange={dropdownOnChange}
          dropdownOpened={dropdownOpened}
          setDropdownOpened={setDropdownOpened}
          checkboxOnChange={checkboxOnChange}
          colorOnChange={colorOnChange}
        />
        <ToneMappingComponent
          scene={scene}
          dropdownOnChange={dropdownOnChange}
          dropdownOpened={dropdownOpened}
          setDropdownOpened={setDropdownOpened}
          inputOnChange={inputOnChange}
        />
        <PostProcessingComponent
          scene={scene}
          checkboxOnChange={checkboxOnChange}
          colorOnChange={colorOnChange}
          dropdownOnChange={dropdownOnChange}
          dropdownOpened={dropdownOpened}
          setDropdownOpened={setDropdownOpened}
          inputOnChange={inputOnChange}
        />
        <ScriptComponent
          target={scene}
          isObjectScripting={isObjectScripting}
          addUserScript={addUserScript}
          editUserScript={editUserScript}
          removeUserScript={removeUserScript}
          textOnChange={textOnChange}
          setScriptIndex={setScriptIndex}
        />
      </div>
    );
  }

  if (!selectedObject || !selectedObject.parent) {
    return null;
  }

  // SelectedObject Existing Case
  const {type, name} = selectedObject;

  const objectType = selectedObject.userData.type;
  const pId = selectedObject.userData.pId || "";
  const isMeshObject = type === "Mesh" && objectType === "mesh";

  return (
    <div className={styles.container}>
      <TitleComponent isObjectScripting={isObjectScripting} selectedObject={selectedObject} textOnChange={textOnChange} />
      <ObjectDataComponent selectedObject={selectedObject} />
      <TransformComponent
        selectedObject={selectedObject}
        transformOnChange={transformOnChange}
        dropdownOnChange={dropdownOnChange}
        dropdownOpened={dropdownOpened}
        setDropdownOpened={setDropdownOpened}
        checkboxOnChange={checkboxOnChange}
      />
      <LightComponent
        selectedObject={selectedObject}
        colorOnChange={colorOnChange}
        transformOnChange={transformOnChange}
        checkboxOnChange={checkboxOnChange}
        inputOnChange={inputOnChange}
      />
      <CameraComponent selectedObject={selectedObject} checkboxOnChange={checkboxOnChange} inputOnChange={inputOnChange} />
      {type === "Mesh" && objectType !== "video" && (
        <>
          <MaterialPropertyComponent
            selectedObject={selectedObject}
            colorOnChange={colorOnChange}
            inputOnChange={inputOnChange}
            dropdownOnChange={dropdownOnChange}
            dropdownOpened={dropdownOpened}
            setDropdownOpened={setDropdownOpened}
          />
          <TextureComponent
            selectedObject={selectedObject}
            transformOnChange={transformOnChange}
            inputOnChange={inputOnChange}
            checkboxOnChange={checkboxOnChange}
          />
        </>
      )}

      {type === "Mesh" && name === "Portal" && <PortalComponent pId={pId} textOnChange={textOnChange} />}
      {objectType !== "audio" && objectType !== "particleSystem" && !isMeshObject && (
        <AppearanceComponent selectedObject={selectedObject} inputOnChange={inputOnChange} />
      )}
      {objectType !== "audio" && objectType !== "particleSystem" && !isMaterialTabOpen && (
        <BehaviorComponent
          isObjectScripting={isObjectScripting}
          selectedObject={selectedObject}
          checkboxOnChange={checkboxOnChange}
          dropdownOnChange={dropdownOnChange}
          dropdownOpened={dropdownOpened}
          setDropdownOpened={setDropdownOpened}
        />
      )}
      <VideoComponent selectedObject={selectedObject} textOnChange={textOnChange} checkboxOnChange={checkboxOnChange} />
      <PhysicsComponent
        isObjectScripting={isObjectScripting}
        selectedObject={selectedObject}
        inputOnChange={inputOnChange}
        checkboxOnChange={checkboxOnChange}
        dropdownOnChange={dropdownOnChange}
        dropdownOpened={dropdownOpened}
        setDropdownOpened={setDropdownOpened}
      />
      <EmitterPropertyComponent
        selectedObject={selectedObject}
        transformOnChange={transformOnChange}
        dropdownOnChange={dropdownOnChange}
        dropdownOpened={dropdownOpened}
        setDropdownOpened={setDropdownOpened}
      />
      <ParticlePropertyComponent selectedObject={selectedObject} inputOnChange={inputOnChange} transformOnChange={transformOnChange} />
      <ParticleBehaviorComponent
        selectedObject={selectedObject}
        inputOnChange={inputOnChange}
        colorOnChange={colorOnChange}
        transformOnChange={transformOnChange}
        checkboxOnChange={checkboxOnChange}
      />
      <SpriteTextComponent
        selectedObject={selectedObject}
        textOnChange={textOnChange}
        colorOnChange={colorOnChange}
        dropdownOnChange={dropdownOnChange}
        dropdownOpened={dropdownOpened}
        setDropdownOpened={setDropdownOpened}
        inputOnChange={inputOnChange}
      />
      <AudioComponent
        selectedObject={selectedObject}
        inputOnChange={inputOnChange}
        checkboxOnChange={checkboxOnChange}
        dropdownOnChange={dropdownOnChange}
        dropdownOpened={dropdownOpened}
        setDropdownOpened={setDropdownOpened}
        // isAudioPlaying={isAudioPlaying}
        // playPreviewAudio={playPreviewAudio}
        // stopPreviewAudio={stopPreviewAudio}
      />
      <ScriptComponent
        target={selectedObject}
        isObjectScripting={isObjectScripting}
        addUserScript={addUserScript}
        editUserScript={editUserScript}
        removeUserScript={removeUserScript}
        textOnChange={textOnChange}
        setScriptIndex={setScriptIndex}
      />
    </div>
  );
};

export default View;
